import { Link, Navigate } from "react-router-dom";
import { Users, Car, UserCheck, CheckCircle2, Circle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { PageHeader, LoadingState, ErrorState, StatCard, Card, StatusBadge } from "@/components/common";
import { PublicLinkCard } from "@/components/pro/PublicLinkCard";
import { TodayWidgets } from "@/modules/scheduling/TodayWidgets";
import { JobCounts } from "@/modules/interventions/JobCounts";
import { FinanceBlock, UnpaidCard } from "@/modules/dashboards/FinanceBlock";
import { HomeBlock, TodoBlock } from "@/modules/dashboards/Home";
import { useGarage } from "@/context/GarageContext";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/lib/useFetch";
import { fmtDate } from "@/lib/format";
import { t } from "@/i18n";
import { ShareLinkCard } from "@/components/pro/ShareLinkCard";

export const STEP_ORDER = ["manager_account", "garage", "branding", "billing", "hours_services", "employees", "preview", "activate"];

function SetupGuide({ setup }) {
  const { canManage } = useGarage();
  const next = setup.steps.find((s) => !s.done);
  const pct = Math.round((setup.done_count / setup.total) * 100);
  return (
    <Card className="border-teal-200" testId="setup-guide">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-700">{t("setup.eyebrow")}</p>
          <h2 className="mt-1 text-xl font-bold text-slate-900">{t("setup.title")}</h2>
          <p className="mt-1 text-sm text-slate-600" data-testid="setup-progress-text">{t("setup.progress", { done: setup.done_count, total: setup.total })}</p>
        </div>
        {canManage && next ? (
          <Button asChild data-testid="continue-setup-button"><Link to={`/pro/onboarding?step=${STEP_ORDER.indexOf(next.key) + 1}`}>{t("setup.continue")} <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
        ) : (
          !canManage && <p className="text-sm text-slate-500">{t("setup.managerOnly")}</p>
        )}
      </div>
      <Progress value={pct} className="mt-5 h-2" />
      <ul className="mt-5 grid gap-2 sm:grid-cols-2">
        {setup.steps.map((s, i) => (
          <li key={s.key} className="flex items-center gap-2 text-sm" data-testid={`setup-step-${s.key}`}>
            {s.done ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-300" />}
            <span className={s.done ? "text-slate-500 line-through" : "text-slate-800"}>{i + 1}. {t(`setup.steps.${s.key}`)}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export default function ProDashboard() {
  const { access } = useGarage();
  if (access.role === "mechanic" && window.innerWidth < 1024) return <Navigate to="/pro/my-day" replace />;
  return <Dashboard />;
}

function Dashboard() {
  const { garage, access, isManager, hasFeature } = useGarage();
  const { user } = useAuth();
  const { data, error, loading, reload } = useFetch("/garage/dashboard");
  const plan = useFetch(isManager ? "/garage/plan" : null);
  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div className="space-y-8">
      <PageHeader eyebrow={garage.name} title={t("dashboard.hello", { name: user.name?.split(" ")[0] || "" })} subtitle={t("dashboard.subtitle")} />
      {!data.setup.complete && <SetupGuide setup={data.setup} />}
      <ShareLinkCard />
      <HomeBlock title={t("b13.home.today")} testId="home-today"><JobCounts /><TodayWidgets tz={garage.timezone} /></HomeBlock>
      <HomeBlock title={t("b13.home.todo")} testId="home-todo"><TodoBlock /></HomeBlock>
      {isManager && <HomeBlock title={t("b13.home.numbers")} testId="home-numbers" collapsible>
        {hasFeature("quotes_and_invoices") && <><UnpaidCard /><FinanceBlock /></>}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard label={t("dashboard.customers")} value={data.customers_count} icon={Users} testId="dashboard-customers-count" />
          <StatCard label={t("dashboard.vehicles")} value={data.vehicles_count} icon={Car} testId="dashboard-vehicles-count" />
          <StatCard label={t("dashboard.portalAccounts")} value={data.portal_accounts} icon={UserCheck} testId="dashboard-portal-accounts" />
        </div>
      <div className={`grid grid-cols-1 gap-6 [&>*]:min-w-0 ${isManager ? "lg:grid-cols-[1.3fr_1fr]" : ""}`}>
        <Card>
          <h2 className="mb-4 text-lg font-bold text-slate-900">{t("dashboard.publicLink")}</h2>
          <PublicLinkCard />
        </Card>
        {isManager && <Card testId="dashboard-plan-card">
          <h2 className="mb-4 text-lg font-bold text-slate-900">{t("dashboard.plan")}</h2>
          {plan.data ? (
            <div className="space-y-2 text-sm">
              <p className="flex items-center justify-between"><span className="text-slate-500">{t("admin.col.plan")}</span><strong>{plan.data.plan?.name || "—"}</strong></p>
              <p className="flex items-center justify-between"><span className="text-slate-500">{t("admin.col.subscription")}</span><StatusBadge status={access.subscription_status} /></p>
              {plan.data.subscription.trial_ends_at && <p className="flex items-center justify-between"><span className="text-slate-500">{t("dashboard.trialEnds")}</span><strong>{fmtDate(plan.data.subscription.trial_ends_at, garage.timezone)}</strong></p>}
              <p className="flex items-center justify-between"><span className="text-slate-500">{t("employees.seats")}</span><strong>{plan.data.seats_used} / {plan.data.plan?.max_internal_users ?? "—"}</strong></p>
            </div>
          ) : <LoadingState />}
        </Card>}
      </div>
      </HomeBlock>}
    </div>
  );
}
